import React, { useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import { useUserContext } from "@/contexts/UserContextProvider";
import { cn } from "@/lib/utils";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  PlusIcon,
  RefreshCcw,
  XIcon,
  TriangleAlert,
  ChevronDown,
  DownloadIcon,
} from "lucide-react";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Formik, Form, Field, FieldProps } from "formik";
import { Dictionary } from "lodash";
import { toast } from "sonner";

import FileUploadField from "@/components/common/FileUpload/FileUpload";
import { Info } from "lucide-react";
import ForwardIcon from "@/assets/ForwardIcon";
import {
  downloadReferencesZipFile,
  downloadIndividualFile,
} from "@/helpers/downloadHelper";

import { CheckUploadsQuery_LinkUploadQuery } from "@/__generated__/graphql";
import { ShotDetailsInput } from "@/__generated__/graphql";
import { FileAttachment, Service } from "../types";
import {
  ProjectMediaUpload,
  ProjectMediaUploadStatus,
} from "@/__generated__/graphql";
import { getServiceColor } from "@/helpers/utils";
import { getShotFootageName } from "@/helpers/shot";
import { getFileNameFromPath } from "@/helpers/filename";
import { Shot as ShotType } from "@/helpers/shot";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import { Theme, useTheme } from "@/contexts/ThemeContext";

export const CHECK_UPLOADS_QUERY = gql`
  query CheckUploadsQuery_LinkUpload($id: SmartID!, $uploadId: SmartID) {
    project(id: $id) {
      __typename
      id
      uploads(id: $uploadId) {
        id
        isFinished
        isSyncFinished
        isDetectionFinished
        isDesktopUpload
        status
        userErrorMessages
        additionalMediaKeys
        shots {
          __typename
          id
          name
          frameCount
          frameKeys
          created
          frameKeys
          defaultReferenceFrameKeys
          videoStorageKey
          serviceIds
        }
      }
    }
  }
`;

type CheckUploadsQuery_Project = NonNullable<
  CheckUploadsQuery_LinkUploadQuery["project"]
>;
type CheckUploadsQuery_Upload = CheckUploadsQuery_Project["uploads"][number];
type Shot = CheckUploadsQuery_Upload["shots"][number];

const referenceFormSchema = z.object({
  fileAttachments: z.array(z.string()),
});

interface ReviewMediaProps extends Omit<CheckUploadsProps, "setNewUploadData"> {
  setNewUploadData: (shots: Shot[], additionalMediaKeys: string[]) => void;
  upload?: CheckUploadsQuery_Upload;
}

const ReviewMedia: React.FC<ReviewMediaProps> = ({
  projectId,
  shots,
  services,
  updateServiceReferenceFiles,
  serviceReferenceFiles,
  onNext,
  submitting,
  shotDetails,
  updateShots,
  existingShots,
  onReset,
  onSkipBidding,
  forSubTask = false,
  upload,
}) => {
  const { theme } = useTheme();
  const { isHotspringClientProfile, isHotspringVendorProfile } =
    useUserContext();
  const isInternalTeam = isHotspringClientProfile || isHotspringVendorProfile;

  const getUrlForKey = (key: string) => {
    if (!upload?.additionalMediaKeys) return null;
    const media = (upload.additionalMediaKeys as string[][]).find(
      (m: string[]) => m[0] === key,
    );
    return media ? media[1] : null;
  };

  const handleDownloadAllReferences = (serviceId: string) => {
    const filesToDownload: { name: string; url: string }[] = [];

    serviceReferenceFiles[serviceId].additionalMediaKeys.forEach((key) => {
      const url = getUrlForKey(key);
      if (url) {
        filesToDownload.push({ name: getFileNameFromPath(key), url });
      }
    });

    serviceReferenceFiles[serviceId].fileAttachments
      .filter((f) => f.selected)
      .forEach((f) => {
        if (f.url) {
          filesToDownload.push({ name: f.name, url: f.url });
        }
      });

    if (filesToDownload.length > 0) {
      downloadReferencesZipFile(filesToDownload);
    } else {
      toast.error("No valid download URLs found for references.");
    }
  };

  const handleDownloadIndividualReference = async (
    name: string,
    url: string | null | undefined,
  ) => {
    if (!url) return;
    await downloadIndividualFile(name, url);
  };

  useEffect(() => {
    if (services?.length) {
      services.forEach((service) => {
        if (!serviceReferenceFiles[service.id]) {
          updateServiceReferenceFiles(service.id, {
            additionalMediaKeys: [],
            fileAttachments: [],
            referenceShots: [],
          });
        }
      });
    }
  }, []);

  const isDuplicateShot = (shot: Shot, service: Service) => {
    return !!existingShots?.find(
      (s) =>
        s.name === shot.name && s.serviceIds?.includes(service?.id as string),
    );
  };

  const removeShot = (shot: Shot, serviceId: string) => {
    const newShotDetails = {
      ...shotDetails,
      [serviceId]: (shotDetails[serviceId] || []).filter(
        (sd) => sd.shotId !== shot.id,
      ),
    };

    const shotsResult = {
      ...shots,
      [serviceId]: (shots[serviceId] || []).filter((s) => s.id !== shot.id),
    };
    updateShots(shotsResult, newShotDetails);
  };

  const removeServiceReferenceShot = (serviceId: string, shot: Shot) => {
    updateServiceReferenceFiles(serviceId, {
      referenceShots: serviceReferenceFiles[serviceId].referenceShots.filter(
        (s) => s.id !== shot.id,
      ),
    });
  };

  const removeServiceAdditionalMediaKey = (serviceId: string, key: string) => {
    updateServiceReferenceFiles(serviceId, {
      additionalMediaKeys: serviceReferenceFiles[
        serviceId
      ].additionalMediaKeys.filter((k) => k !== key),
    });
  };

  const removeServiceFileAttachment = (
    serviceId: string,
    file: FileAttachment,
  ) => {
    updateServiceReferenceFiles(serviceId, {
      fileAttachments: serviceReferenceFiles[serviceId].fileAttachments.map(
        (f) => (f.id === file.id ? { ...f, selected: false } : f),
      ),
    });
  };

  const servicesBtn = (service: Service) => {
    if (!service?.subServices.length) return null;
    const serviceShotDetails = shotDetails[service.id];
    const selectedSubServiceIds = service.subServices
      .map((subService) => {
        if (
          serviceShotDetails.every(
            (sd) =>
              !!sd.subServices?.find(
                (sub) => sub.subServiceId === subService.id,
              ),
          )
        )
          return subService.id;
        return null;
      })
      .filter((id) => !!id);

    return (
      <DropdownMenu>
        <DropdownMenuTrigger className="border border-border rounded cursor-pointer bg-button hover:bg-secondary p-1">
          <PlusIcon className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="p-4 flex flex-col gap-4"
          style={{ width: "100%" }}
          align="start"
        >
          {service.subServices.map((subService) => (
            <Button
              key={subService.id}
              className="border border-service-3d-tracking !py-0 flex gap-2 text-service-3d-tracking items-center w-30"
              onClick={() => {
                updateShots(shots, {
                  ...shotDetails,
                  [service.id]: serviceShotDetails.map((shotDetail) => {
                    const shotSubServices =
                      shotDetail.subServices?.filter(
                        (s) => s.subServiceId !== subService.id,
                      ) || [];
                    if (!selectedSubServiceIds.includes(subService.id))
                      shotSubServices.push({
                        priority: shotDetail.priority,
                        subServiceId: subService.id,
                      });
                    return {
                      ...shotDetail,
                      subServices: shotSubServices,
                    };
                  }),
                });
              }}
            >
              {subService.name}
              {selectedSubServiceIds.includes(subService.id) && (
                <XIcon className="text-service-3d-tracking" />
              )}
            </Button>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="flex flex-col flex-grow h-full">
      <div className="flex flex-col gap-[20px] flex-grow  max-h-[calc(100%-160px)] overflow-scroll overflow-x-hidden">
        {services.map((service) => (
          <ReviewMediaSkeleton
            service={service}
            key={service.id}
            onReset={onReset}
          >
            <div className="w-full m-5 mt-0 flex gap-[24px] text-secondary-foreground text-base">
              <div className="!w-[calc(70%-1px)] flex flex-col gap-2">
                <div className="flex">
                  <div className="w-[40%]">
                    Shot Name ({shots[service.id].length})
                  </div>
                  <div className="w-[40%]">Footage</div>
                  {!!service.subServices.length && (
                    <div className="w-[20%] flex items-end justify-end gap-2">
                      <span>Sub-service</span>
                      {servicesBtn(service)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 text-sm overflow-auto max-h-[calc(100vh-350px)]">
                  {shots[service.id].map((shot) => {
                    const name = shot && shot.name;
                    const shotDetail = shotDetails[service.id].find(
                      (s) => s.shotId === shot.id,
                    );
                    const selectedSubServiceIds =
                      shotDetail?.subServices?.map(
                        (subService) => subService.subServiceId,
                      ) || [];
                    return (
                      <div key={`${service.id}${shot.id}`} className="flex">
                        <div className="w-[40%] flex gap-2 items-center">
                          <div className="border border-accent-foreground px-3 py-3 rounded-[8px] w-[80%] flex justify-between items-center">
                            <div className="w-full">
                              <LongContentWrapper>{name}</LongContentWrapper>
                            </div>
                            {isDuplicateShot(shot, service) && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <TriangleAlert className="text-[#FF0000] h-5 w-5" />
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="bottom"
                                    className="!p-2 !bg-primary border border-accent-foreground"
                                  >
                                    {shot.name} for {service.name} already
                                    exists.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {!forSubTask && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger
                                  className="border border-accent-foreground h-full p-3 rounded-md cursor-pointer bg-button hover:bg-secondary"
                                  onClick={() => removeShot(shot, service.id)}
                                >
                                  <XIcon className="h-4 w-4 text-foreground" />
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="!p-2 !bg-primary border border-accent-foreground"
                                >
                                  Remove Task
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <div className="w-[40%] flex gap-2 items-center">
                          <div className="border border-accent-foreground px-3 py-3 rounded-[8px] w-[80%]">
                            <LongContentWrapper>
                              {getShotFootageName(shot as ShotType)}
                            </LongContentWrapper>
                          </div>
                          {!forSubTask && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger
                                  className="border border-accent-foreground h-full p-3 rounded-md cursor-pointer bg-button hover:bg-secondary"
                                  onClick={() => {
                                    removeShot(shot, service.id);
                                    updateServiceReferenceFiles(
                                      service?.id as string,
                                      {
                                        referenceShots: [
                                          ...serviceReferenceFiles[
                                            service?.id as string
                                          ].referenceShots,
                                          shot,
                                        ],
                                      },
                                    );
                                  }}
                                >
                                  <ForwardIcon
                                    color={
                                      theme === Theme.Light
                                        ? "#1d1e1f"
                                        : undefined
                                    }
                                  />
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="!p-2 !bg-primary border border-accent-foreground"
                                >
                                  Add shot to references
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <div className="w-[20%]">
                          {!!service.subServices.length && (
                            <DropdownMenu>
                              <DropdownMenuTrigger className="w-full h-full">
                                <div className="w-full h-full flex items-center gap-[8px]">
                                  {!!selectedSubServiceIds.length && (
                                    <div
                                      className={cn(
                                        "h-full w-full text-service-3d-tracking border border-service-3d-tracking",
                                        "px-2 py-1 text-sm rounded-[8px] flex items-center justify-center",
                                      )}
                                    >
                                      {selectedSubServiceIds.length} Services
                                      <ChevronDown />
                                    </div>
                                  )}
                                  <Button
                                    className={cn(
                                      "h-full border border-accent-foreground",
                                      selectedSubServiceIds.length
                                        ? "w-auto"
                                        : "w-full",
                                    )}
                                  >
                                    <PlusIcon />
                                  </Button>
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                className="p-4 flex flex-col gap-4"
                                style={{ width: "100%" }}
                                align="start"
                              >
                                {service.subServices.map((subService) => (
                                  <Button
                                    key={subService.id}
                                    className="border border-service-3d-tracking !py-0 flex gap-2 text-service-3d-tracking items-center w-30"
                                    onClick={() => {
                                      updateShots(shots, {
                                        ...shotDetails,
                                        [service.id]: (
                                          shotDetails[service.id] || []
                                        ).map((shotDetail) => {
                                          if (shotDetail.shotId !== shot.id)
                                            return shotDetail;

                                          const shotSubServices =
                                            shotDetail.subServices?.filter(
                                              (s) =>
                                                s.subServiceId !==
                                                subService.id,
                                            ) || [];

                                          if (
                                            !selectedSubServiceIds.includes(
                                              subService.id,
                                            )
                                          ) {
                                            shotSubServices.push({
                                              priority: shotDetail.priority,
                                              subServiceId: subService.id,
                                            });
                                          }

                                          return {
                                            ...shotDetail,
                                            subServices: shotSubServices,
                                          };
                                        }),
                                      });
                                    }}
                                  >
                                    {subService.name}
                                    {selectedSubServiceIds.includes(
                                      subService.id,
                                    ) && (
                                      <XIcon className="text-service-3d-tracking" />
                                    )}
                                  </Button>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="min-w-[1px] h-full bg-accent-foreground" />
              <div className="!w-[30%] mr-10 flex flex-col gap-[20px]">
                <div className="flex gap-2 items-bottom">
                  <div className="flex gap-2">
                    References
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="p-0 m-0 ml-[-4px]">
                          <Info className="h-4 w-4 mt-0.5" />
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="!p-2 !bg-primary border border-accent-foreground"
                        >
                          This area contains all non-shot assets we found in the
                          path specified. <br />
                          These files will be included in your bid package.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {isInternalTeam && !upload?.isDesktopUpload && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            className="h-6 w-6 p-0 hover:bg-transparent"
                            onClick={() =>
                              handleDownloadAllReferences(service.id)
                            }
                          >
                            <DownloadIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="!p-2 !bg-primary border border-accent-foreground"
                        >
                          Download all references
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="flex flex-col gap-[12px] text-sm overflow-auto max-h-[calc(100vh-350px)]">
                  {serviceReferenceFiles[service.id].additionalMediaKeys.map(
                    (key) => (
                      <div
                        key={`${service.id}${key}`}
                        className="flex justify-between bg-tertiary rounded-[8px] px-3 py-2 items-center"
                      >
                        <LongContentWrapper className="w-90">
                          {getFileNameFromPath(key)}
                        </LongContentWrapper>
                        <div className="flex gap-2">
                          {getUrlForKey(key) &&
                            isInternalTeam &&
                            !upload?.isDesktopUpload && (
                              <Button
                                className="bg-transparent h-4 w-4"
                                onClick={() =>
                                  handleDownloadIndividualReference(
                                    getFileNameFromPath(key),
                                    getUrlForKey(key),
                                  )
                                }
                              >
                                <DownloadIcon />
                              </Button>
                            )}
                          <Button
                            className="bg-transparent h-4 w-4"
                            onClick={() =>
                              removeServiceAdditionalMediaKey(service.id, key)
                            }
                          >
                            <XIcon />
                          </Button>
                        </div>
                      </div>
                    ),
                  )}
                  {serviceReferenceFiles[service.id].referenceShots.map(
                    (shot) => (
                      <div
                        key={`${service.id}${shot.id}`}
                        className="flex justify-between bg-tertiary rounded-[8px] px-3 py-2 items-center"
                      >
                        <LongContentWrapper className="w-90">
                          {getShotFootageName(shot as ShotType)}
                        </LongContentWrapper>
                        <Button
                          className="bg-transparent h-4 w-4"
                          onClick={() =>
                            removeServiceReferenceShot(service.id, shot)
                          }
                        >
                          <XIcon />
                        </Button>
                      </div>
                    ),
                  )}
                  {serviceReferenceFiles[service.id].fileAttachments
                    .filter((f) => f.selected)
                    .map((file) => (
                      <div className="flex justify-between bg-tertiary rounded-[8px] px-3 py-2 items-center">
                        <LongContentWrapper className="w-90">
                          {file.name}
                        </LongContentWrapper>
                        <div className="flex gap-2">
                          {file.url && isInternalTeam && (
                            <Button
                              className="bg-transparent h-4 w-4"
                              onClick={() =>
                                handleDownloadIndividualReference(
                                  file.name,
                                  file.url,
                                )
                              }
                            >
                              <DownloadIcon />
                            </Button>
                          )}
                          <Button
                            className="bg-transparent h-4 w-4"
                            onClick={() =>
                              removeServiceFileAttachment(service.id, file)
                            }
                          >
                            <XIcon />
                          </Button>
                        </div>
                      </div>
                    ))}
                  <Formik<{ fileAttachments: string[] }>
                    initialValues={{ fileAttachments: [] }}
                    validationSchema={referenceFormSchema}
                    onSubmit={() => {}}
                  >
                    <Form>
                      <Field
                        name="fileAttachments"
                        render={(props: FieldProps) => (
                          <FileUploadField
                            {...props}
                            projectId={projectId}
                            showFiles={false}
                            showLabel
                            onChange={(e: {
                              target: {
                                name: string;
                                value: string[];
                                keys: Dictionary<File>;
                              };
                            }) => {
                              const {
                                target: { value, keys },
                              } = e;
                              const existingFileAttachmentIds =
                                serviceReferenceFiles[
                                  service.id
                                ].fileAttachments.map((f) => f.id);
                              const newAttachments = value
                                .filter(
                                  (id) =>
                                    existingFileAttachmentIds.indexOf(id) ===
                                    -1,
                                )
                                .map((id) => ({
                                  id,
                                  name: keys[id].name,
                                  selected: true,
                                }));
                              updateServiceReferenceFiles(service.id, {
                                fileAttachments: [
                                  ...serviceReferenceFiles[service.id]
                                    .fileAttachments,
                                  ...newAttachments,
                                ],
                              });
                            }}
                          />
                        )}
                      />
                    </Form>
                  </Formik>
                </div>
              </div>
            </div>
          </ReviewMediaSkeleton>
        ))}
      </div>
      <div className="shrink-0 bg-primary py-[16px] px-[24px] flex gap-3 justify-end rounded-b-[24px] border-t border-border">
        {onSkipBidding && services.length === 1 && (
          <Button
            className="text-service-paint p-3 rounded border !border-service-paint transition hover:opacity-80 disabled:opacity-50"
            onClick={() => onSkipBidding()}
          >
            Skip Bidding
          </Button>
        )}
        {onNext && (
          <Button
            className="text-white py-[4px] px-[30px] rounded transition !bg-service-paint hover:opacity-80 disabled:opacity-50"
            onClick={() => onNext()}
            disabled={submitting}
          >
            {forSubTask ? "Submit" : "Next"}
          </Button>
        )}
      </div>
    </div>
  );
};

interface ReviewMediaSkeletonProps {
  children: React.ReactNode;
  onReset?: (serviceId: string) => void;
  service: Service;
  className?: string;
}

const ReviewMediaSkeleton: React.FC<ReviewMediaSkeletonProps> = ({
  children,
  onReset,
  service,
  className,
}) => (
  <div className={cn("flex flex-col bg-primary", className)}>
    <div className="flex w-full justify-between">
      <div
        className="ml-6 w-min h-min py-1 px-[20px] rounded-b-[12px] left-6 text-white text-nowrap"
        style={{
          background: getServiceColor(service.name),
        }}
      >
        {service.name}
      </div>
      <Button
        className="my-2 mx-4 border border-accent-foreground rounded-[8px] bg-transparent"
        onClick={() => onReset && onReset(service.id)}
      >
        <RefreshCcw />
        Reset
      </Button>
    </div>
    {children}
  </div>
);

export interface CheckUploadsProps {
  projectId: string;
  uploadId?: string;
  shots: { [serviceId: string]: Shot[] };
  updateShots: (
    shots: { [serviceId: string]: Shot[] },
    shotDetails: { [serviceId: string]: ShotDetailsInput[] },
  ) => void;
  setNewUploadData: (shots: Shot[], additionalMediaKeys: string[]) => void;
  shotDetails: { [serviceId: string]: ShotDetailsInput[] };
  onNext?: () => void;
  onReset: (serviceId: string) => void;
  forSubTask?: boolean;
  submitting?: boolean;
  existingShots?: Shot[];
  services: Service[];
  serviceReferenceFiles: {
    [serviceId: string]: {
      additionalMediaKeys: string[];
      fileAttachments: FileAttachment[];
      referenceShots: Shot[];
    };
  };
  updateServiceReferenceFiles: (
    serviceId: string,
    updates: Partial<{
      additionalMediaKeys: string[];
      fileAttachments: FileAttachment[];
      referenceShots: Shot[];
    }>,
  ) => void;
  onSkipBidding?: () => void;
}
const CheckUploads: React.FC<CheckUploadsProps> = ({
  projectId,
  uploadId,
  shots,
  setNewUploadData,
  services,
  ...restProps
}) => {
  const { data, loading, error, stopPolling } = useQuery(CHECK_UPLOADS_QUERY, {
    variables: { id: projectId, uploadId },
    skip: !uploadId,
    pollInterval: 500,
  });
  if (loading) return null;
  if (error) return <div>{`Error! ${error.message}`}</div>;

  const project = data?.project;
  const upload = project?.uploads?.[0];

  if (!upload || !uploadId) {
    stopPolling?.();
    return (
      <ReviewMedia
        projectId={projectId}
        shots={shots}
        setNewUploadData={setNewUploadData}
        services={services}
        upload={upload}
        {...restProps}
      />
    );
  }

  const { status, isDetectionFinished, userErrorMessages } = upload;

  if (status === "ERROR" || userErrorMessages.length) {
    stopPolling?.();
    return (
      <ReviewMediaSkeleton service={services[0]} className="rounded-b-[24px]">
        <div className="p-6 pt-0 rounded-b-[24px]">
          {userErrorMessages.length ? (
            <div>
              <h3 className="text-red-500 mb-2">Upload Errors:</h3>
              <ul className="text-sm text-red-600">
                {userErrorMessages.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          ) : (
            <h3 className="add-animated-ellipses">Error syncing server</h3>
          )}
        </div>
      </ReviewMediaSkeleton>
    );
  }

  if (
    (status === "IN_PROGRESS" || status === "NOT_STARTED") &&
    !isDetectionFinished
  ) {
    return (
      <>
        {services.map((service) => (
          <ReviewMediaSkeleton
            service={service}
            key={service.id}
            className="rounded-b-[24px]"
          >
            <div className="p-6 pt-0 rounded-b-[24px]">
              <h3 className="add-animated-ellipses">Sync in progress...</h3>
            </div>
          </ReviewMediaSkeleton>
        ))}
      </>
    );
  }

  stopPolling?.();

  if (!project) {
    return (
      <>
        {services.map((service) => (
          <ReviewMediaSkeleton
            service={service}
            key={service.id}
            className="rounded-b-[24px]"
          >
            <div className="p-6 pt-0 rounded-b-[24px]">
              <h3 className="add-animated-ellipses">
                Project not found, please contact support
              </h3>
            </div>
          </ReviewMediaSkeleton>
        ))}
      </>
    );
  }

  return (
    <>
      <ReviewMedia
        projectId={projectId}
        uploadId={uploadId}
        shots={shots}
        setNewUploadData={setNewUploadData}
        services={services}
        upload={upload}
        {...restProps}
      />
      <MergeShots
        status={status}
        upload={upload}
        uploadId={uploadId}
        setNewUploadData={setNewUploadData}
      />
    </>
  );
};

interface MergeShotProps {
  status: ProjectMediaUploadStatus;
  upload: ProjectMediaUpload;
  uploadId: string;
  setNewUploadData: CheckUploadsProps["setNewUploadData"];
}

const MergeShots: React.FC<MergeShotProps> = ({
  status,
  upload,
  uploadId,
  setNewUploadData,
}) => {
  const prevUploadIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (prevUploadIdRef.current === uploadId) return;
    prevUploadIdRef.current = uploadId;
    const newShots = (upload?.shots?.filter(Boolean) ?? []) as Shot[];
    const newKeys = upload.additionalMediaKeys.map((v) => v[0]);
    setNewUploadData(newShots, newKeys);
  }, [status, uploadId, upload, setNewUploadData]);
  return null;
};

export default CheckUploads;
